import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import EditCostModal from '../components/EditCostModal'; // We need this for the "Update Cost" button
import '../Styles/LossReport.css';

const API_URL = process.env.REACT_APP_API_URL;
const getLineId = (line) => {
    const raw = line?.recordid ?? line?.record_id ?? null;
    return raw == null ? null : String(raw);
  };
const idsFromLines = (arr) => arr.map(getLineId).filter((id) => id !== null);
// --- Component 1: The Modal for Bulk Corrections ---
// This component handles the pop-up for correcting historical sales lines.
// --- Component 1: The Modal for Bulk Corrections (fixed) ---
// --- Component 1: The Modal for Bulk Corrections (robust id + clickable) ---
const BulkCorrectionModal = ({ isOpen, onClose, product, headers, onUpdateSuccess, finYears }) => {
  const [lines, setLines] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set()); // UI selection keys (strings)
  const [newCostPrice, setNewCostPrice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedFinYear, setSelectedFinYear] = useState('');

  // ---------- ID helpers (now allow STRING ids) ----------
  const extractRecordId = (line) =>
    line?.recordid ?? line?.record_id ?? line?.recordId ?? line?.RECORDID ?? line?.id ?? null;

  // return a stable UI key; fall back to row index if no id
  const toKey = (line, idx) => {
    const raw = extractRecordId(line);
    return raw == null ? `row-${idx}` : `id-${String(raw)}`;
  };

  // treat any non-empty string as a valid ID
  const validRecordId = (line) => {
    const raw = extractRecordId(line);
    if (raw == null) return null;
    const s = String(raw).trim();
    return s.length ? s : null; // <-- accept string ids
  };

  const allRowKeys = useMemo(() => lines.map((l, i) => toKey(l, i)), [lines]);

  // derived
  const selectedCount = selectedKeys.size;
  const selectedValidIds = useMemo(() => {
    const ids = [];
    lines.forEach((line, idx) => {
      const key = toKey(line, idx);
      if (selectedKeys.has(key)) {
        const id = validRecordId(line);
        if (id !== null) ids.push(id); // keep strings as-is
      }
    });
    return ids;
  }, [lines, selectedKeys]);

  const allSelected = allRowKeys.length > 0 && allRowKeys.every((k) => selectedKeys.has(k));

  // ---------- effects ----------
  useEffect(() => {
    if (isOpen && finYears.length > 0) setSelectedFinYear(finYears[0]);
  }, [isOpen, finYears]);

  useEffect(() => {
    if (isOpen && product && selectedFinYear) {
      const fetchLines = async () => {
        setIsLoading(true);
        setMessage('');
        setLines([]);
        setSelectedKeys(new Set());
        try {
          const res = await axios.get(
            `${API_URL}/reports/loss-making-lines/${product.prodcode}?finyear=${selectedFinYear}`,
            { headers }
          );
          console.log("API Response for Lines:", res.data);
          setLines(res.data || []);
        } catch {
          setMessage('Failed to load sales lines for this year.');
        } finally {
          setIsLoading(false);
        }
      };
      fetchLines();
    }
  }, [isOpen, product, selectedFinYear, headers]);

  // ---------- handlers ----------
  const toggleRow = (key, checked) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const toggleAll = (checked) => {
    setSelectedKeys(checked ? new Set(allRowKeys) : new Set());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedCount === 0 || !newCostPrice) {
      setMessage('Please select lines and enter a new cost price.');
      return;
    }
    if (selectedValidIds.length === 0) {
      setMessage('Selected rows have no valid record IDs to update.');
      return;
    }
    setIsSubmitting(true);
    setMessage('');
    try {
      const payload = {
        // send IDs exactly as returned (strings are fine)
        record_ids: selectedValidIds,
        new_cost_price: parseFloat(newCostPrice),
      };
      const res = await axios.put(`${API_URL}/reports/bulk-update-cost-price`, payload, { headers });
      setMessage(res.data.message);
      setTimeout(() => onUpdateSuccess(), 1500);
    } catch (err) {
      setMessage(err.response?.data?.detail || 'An error occurred.');
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Correct Sales for: {product?.prodname}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="filter-bar modal-filter-bar">
            <div className="form-group">
              <label>Filter by Financial Year</label>
              <select value={selectedFinYear} onChange={(e) => setSelectedFinYear(e.target.value)}>
                {finYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {isLoading ? (
            <p>Loading sales lines...</p>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          onChange={(e) => toggleAll(e.target.checked)}
                          checked={allSelected}
                          aria-label="Select all rows"
                        />
                      </th>
                      <th>Invoice No</th>
                      <th>Date</th>
                      <th>Qty</th>
                      <th>Selling Price/Unit</th>
                      <th>Recorded Cost/Unit</th>
                      <th>Loss/Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line, idx) => {
                      const key = toKey(line, idx);
                      const checked = selectedKeys.has(key);
                      return (
                        <tr key={key}>
                          <td>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => toggleRow(key, e.target.checked)}
                              aria-label={`Select ${line.docno}`}
                            />
                          </td>
                          <td>{line.docno}</td>
                          <td>{new Date(line.docdate).toLocaleDateString('en-GB')}</td>
                          <td>{line.qty}</td>
                          <td>₹{Number(line.selling_price_per_unit).toFixed(2)}</td>
                          <td>₹{Number(line.cost_price).toFixed(2)}</td>
                          <td className="loss-amount">-₹{Number(line.loss_per_unit).toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="update-bar">
                <div className="form-group">
                  <label htmlFor="newCostPrice">New Correct Cost Price</label>
                  <input
                    id="newCostPrice"
                    type="text"
                    inputMode="decimal"
                    value={newCostPrice}
                    onChange={(e) => setNewCostPrice(e.target.value)}
                    placeholder="0.00"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={isSubmitting || selectedCount === 0}
                >
                  {isSubmitting
                    ? 'Updating...'
                    : `Apply to ${selectedCount} Selected Line(s)`}
                </button>
              </div>

              {selectedCount > 0 && selectedValidIds.length < selectedCount && (
                <p className="modal-message">
                  {selectedCount - selectedValidIds.length} selected row(s) don’t have a valid record ID, so they will be ignored when updating.
                </p>
              )}
            </form>
          )}
        </div>

        {message && <p className="modal-message">{message}</p>}
      </div>
    </div>
  );
};


// --- Component 2: The Main Page ---
function ProductCostManager() {
  const [summaryData, setSummaryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [finYears, setFinYears] = useState([]);

  // State for Correction Modal
  const [isCorrectionModalOpen, setIsCorrectionModalOpen] = useState(false);
  const [productToCorrect, setProductToCorrect] = useState(null);

  // State for Edit Cost Modal
  const [isEditCostModalOpen, setIsEditCostModalOpen] = useState(false);
  const [productToEdit, setProductToEdit] = useState(null);

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchFinancialYears = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/financial-years`, { headers });
      setFinYears(res.data.years);
    } catch (error) {
      console.error("Failed to fetch financial years", error);
    }
  }, [headers]);

  const fetchSummary = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/reports/loss-making-summary`, { headers });
      setSummaryData(res.data);
    } catch (error) {
      console.error("Failed to fetch loss-making summary", error);
      alert("Could not load the report. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchFinancialYears();
    fetchSummary();
  }, [fetchFinancialYears, fetchSummary]);

  const handleOpenCorrectionModal = (product) => {
    setProductToCorrect(product);
    setIsCorrectionModalOpen(true);
  };

  const handleOpenEditCostModal = async (product) => {
    try {
      const res = await axios.get(`${API_URL}/products/${product.prodcode}/cost`, { headers });
      setProductToEdit({ ...product, cost_price: res.data.cost_price });
      setIsEditCostModalOpen(true);
    } catch (error) {
      alert("Could not fetch current cost price for this product.");
    }
  };

  const handleCloseAndRefresh = () => {
    setIsCorrectionModalOpen(false);
    setIsEditCostModalOpen(false);
    fetchSummary();
  };

  return (
    <>
      <div className="manage-container">
        <h3>Loss-Making Products Report</h3>
        <p>This report shows products that have been sold for less than their recorded cost price.</p>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product Name</th>
                <th># of Loss Sales</th>
                <th>Total Loss Amount</th>
                <th>First Loss Date</th>
                <th>Last Loss Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="6" className="loading-state">Loading report...</td></tr>
              ) : summaryData.length > 0 ? (
                summaryData.map((p) => (
                  <tr key={p.prodcode}>
                    <td data-label="Product">{p.prodname}</td>
                    <td data-label="# of Loss Sales">{p.loss_sale_count}</td>
                    <td data-label="Total Loss" className="loss-amount">-₹{p.total_loss_amount.toFixed(2)}</td>
                    <td data-label="First Loss">{new Date(p.first_loss_date).toLocaleDateString('en-GB')}</td>
                    <td data-label="Last Loss">{new Date(p.last_loss_date).toLocaleDateString('en-GB')}</td>
                    <td className="actions-cell" data-label="Actions">
                      <button className="btn btn-primary" onClick={() => handleOpenCorrectionModal(p)}>Correct Sales</button>
                      <button className="btn btn-outline" onClick={() => handleOpenEditCostModal(p)}>Update Cost</button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="6" className="empty-state">No products found that were sold at a loss.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <BulkCorrectionModal
        isOpen={isCorrectionModalOpen}
        onClose={() => setIsCorrectionModalOpen(false)}
        product={productToCorrect}
        headers={headers}
        onUpdateSuccess={handleCloseAndRefresh}
        finYears={finYears}
      />
      <EditCostModal
        isOpen={isEditCostModalOpen}
        onClose={() => setIsEditCostModalOpen(false)}
        product={productToEdit}
        onSuccess={handleCloseAndRefresh}
      />
    </>
  );
}

export default ProductCostManager;
